import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { useContext, useEffect, useState } from "react";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import QuestionCard from "./QuestionCard";
import { updateBlockInfoText } from "../axios/axiosFunctions";
import { AssessmentContext } from "../contexts/assessments";

/**
 * Inline editor for the section description that appears in the report
 * (assessment.report.blocksInfo[].text) for a single block (W1).
 */
const SectionTextEditor = ({ assessmentId, blockId, initialText }) => {
  const [text, setText] = useState(initialText || "");
  const [saving, setSaving] = useState(false);
  const [savedAt, setSavedAt] = useState(null);
  const [dirty, setDirty] = useState(false);
  const { refetchAssessments } = useContext(AssessmentContext);

  useEffect(() => {
    setText(initialText || "");
    setDirty(false);
  }, [initialText]);

  const handleSave = async () => {
    if (saving) return;
    setSaving(true);
    const res = await updateBlockInfoText(assessmentId, blockId, text);
    setSaving(false);
    if (res) {
      setSavedAt(Date.now());
      setDirty(false);
      if (refetchAssessments) await refetchAssessments();
      setTimeout(() => setSavedAt(null), 2000);
    }
  };

  return (
    <Box
      sx={{
        backgroundColor: "white",
        padding: "16px",
        borderRadius: "5px",
        marginBottom: "16px",
      }}
    >
      <Typography variant="subtitle2" sx={{ fontWeight: 600, marginBottom: "8px" }}>
        Section description (shown in the report)
      </Typography>
      <TextField
        value={text}
        onChange={(e) => {
          setText(e.target.value);
          setDirty(true);
        }}
        multiline
        minRows={3}
        fullWidth
      />
      <Stack direction="row" spacing={1} alignItems="center" sx={{ marginTop: "8px" }}>
        <Button
          variant="contained"
          size="small"
          color="secondary"
          onClick={handleSave}
          disabled={saving || !dirty}
        >
          {saving ? "Saving..." : "Save"}
        </Button>
        {savedAt && (
          <Stack direction="row" spacing={0.5} alignItems="center">
            <CheckCircleIcon sx={{ color: "green", fontSize: 18 }} />
            <Typography variant="caption" sx={{ color: "green" }}>Saved</Typography>
          </Stack>
        )}
      </Stack>
    </Box>
  );
};

const BlocksAdmin = ({ assessment, blocks }) => {
  const blocksInfo = assessment?.report?.blocksInfo || [];
  const findInfoText = (blockId) => {
    const entry = blocksInfo.find((bi) => String(bi.block) === String(blockId));
    return entry?.text || "";
  };

  return (
    <Box sx={{ marginTop: "40px", marginBottom: "100px" }}>
      {blocks &&
        blocks.map((b, i) => (
          <Accordion
            key={i}
            sx={{
              marginTop: "30px",
              padding: "10px 0px 10px 10px",
              border: "2px solid",
              borderColor: `${b.color}`,
              backgroundColor: `${b.color}`,
            }}
          >
            <AccordionSummary expandIcon={<ExpandMoreIcon sx={{ color: "white" }} />}>
              <Typography variant="h5" fontWeight={600} color="white">
                {b.name}
              </Typography>
            </AccordionSummary>
            <AccordionDetails>
              {assessment?._id && (
                <SectionTextEditor
                  assessmentId={assessment._id}
                  blockId={b._id}
                  initialText={findInfoText(b._id)}
                />
              )}
              <QuestionCard questions={b.questions} color={b.color} blockId={b._id} />
            </AccordionDetails>
          </Accordion>
        ))}
    </Box>
  );
};

export default BlocksAdmin;
